import { useMemo } from 'react';
import { useAppDispatch } from '../hooks';
import { transactionsApiTemplateSlice } from './transactionsApiTemplateSlice';
import { DASHBOARD_CURRENCY } from './transactionFilterSlice';
import {
  GetTransactionsRequestParams,
  injectedTransactionApiEndpoints,
} from './injectedTransactionApiEndpoints';
import { useVehicles } from '../vehicles/useVehicles';
import { Transaction as TransactionDto } from '../../codegen/transactions';
import { Transaction } from '../../models';

export const useTransactions = (request: GetTransactionsRequestParams = {}) => {
  const dispatch = useAppDispatch();
  const transactionsQuery = useGetTransactionsPaginatedQuery(request);
  const {
    vehicles,
    isLoading: isLoadingVehicles,
    isFetching: isFetchingVehicles,
    error: vehiclesError,
  } = useVehicles();

  const resetApiState = () => {
    dispatch(transactionsApiTemplateSlice.util.resetApiState());
  };

  const transactions: Transaction[] = useMemo(() => {
    if (vehicles.length === 0) return [];

    const rawTransactions: TransactionDto[] =
      transactionsQuery.data?.items || [];
    return rawTransactions.map((transaction: TransactionDto) => {
      const vehicle = vehicles.find(
        (v) => v.id === transaction.vehicleDetails.id,
      );
      return {
        ...transaction,
        vehicle,
      };
    });
  }, [vehicles, transactionsQuery.data]);

  return {
    transactions,
    totalTransactions: transactionsQuery.data?.totalItems || 0,
    isLoading:
      transactionsQuery.isLoading ||
      isLoadingVehicles ||
      transactionsQuery.isFetching ||
      isFetchingVehicles,
    error: transactionsQuery.error || vehiclesError,
    resetApiState,
  };
};

export const useCurrencyOptions = () => {
  const { transactions } = useTransactions();
  return useMemo(() => {
    const currencies = Array.from(
      new Set(
        transactions
          .map(
            (transaction) =>
              transaction.completionAmount || transaction.deliveryAmount,
          )
          .filter((amount) => !!amount)
          .map((amount) => amount.currency),
      ),
    ).sort((a, b) => a.localeCompare(b));

    if (currencies.length === 0) {
      currencies.push(DASHBOARD_CURRENCY);
    }
    return currencies.map((c) => ({ id: c, label: c }));
  }, [transactions]);
};

export const { useGetTransactionsPaginatedQuery } =
  injectedTransactionApiEndpoints;
